import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, CheckCircle2 } from "lucide-react";

export default function PublicForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/customers/password-reset/request", {
        tenant_slug: "abrah", email,
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      {sent ? (
        <Card>
          <CardContent className="pt-8 pb-8 text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-100 mb-3">
              <CheckCircle2 size={26} className="text-emerald-700" />
            </div>
            <h1 className="font-display text-2xl font-bold text-gray-900">Check your inbox</h1>
            <p className="text-sm text-gray-600 mt-2">
              If an account exists for <span className="font-semibold">{email}</span>, we&apos;ve sent a password-reset link.
              It expires in 1 hour.
            </p>
            <p className="text-xs text-gray-500 mt-4">Can&apos;t find it? Check your spam folder.</p>
            <div className="mt-6">
              <Link to="/site/login">
                <Button variant="outline">Back to sign in</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-100 mb-3">
              <Mail size={26} className="text-emerald-800" />
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
              Reset your password
            </h1>
            <p className="text-sm text-gray-600 mt-2">
              We&apos;ll email you a secure link to set a new one.
            </p>
          </div>
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label className="text-xs">Email address</Label>
                  <Input
                    required type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email"
                    placeholder="you@example.com"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy || !email}
                  className="w-full bg-emerald-900 hover:bg-emerald-950 text-white"
                  data-testid="forgot-submit">
                  {busy ? "Sending…" : "Send reset link"}
                </Button>
              </form>
              <div className="mt-5 pt-5 border-t border-gray-100 text-sm text-center text-gray-600">
                Remembered it?{" "}
                <Link to="/site/login" className="text-emerald-800 font-semibold hover:underline">
                  Sign in
                </Link>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
